import CloseIcon from '@mui/icons-material/Close';
import {
    Box,
    Drawer,
    IconButton,
    TextField,
    Typography,
} from '@mui/material';
import { isEqual } from 'lodash';
import Image from 'mui-image';
import { useSnackbar } from 'notistack';
import * as React from 'react';
import { useHistory } from 'react-router-dom';

import { CREATE, DataAPIContext } from '../api';
import DeleteIconButton from '../components/DeleteIconButton';
import FixedLinearProgress from '../components/FixedLinearProgress';
import FormActionButtons from '../components/FormActionButtons';
import FormLicenseeContext from '../components/FormLicenseeContext';
import ImageActionButton from '../components/ImageActionButton';
import LatLngPicker from '../components/LatLngPicker';
import LicenseeFormField from '../components/LicenseeFormField';
import PageMeta from '../components/PageMeta';
import SystemTagsDropdown from '../components/SystemTagsDropdown';
import useLeavePrompt from '../components/useLeavePrompt';
import { ContextStore } from '../store/contextStore';
import { toCoord } from './utils';

// What the leave prompt compares against the empty form.
const formSnapshot = (f) => ({
    name: f.name,
    label: f.label,
    description: f.description,
    warning: f.warning,
    latitude: toCoord(f.latitude),
    longitude: toCoord(f.longitude),
    tags: f.tags.map((t) => t.id),
    tourOperator: f.tourOperator,
    imageChanged: f.imageFile !== null,
});

const BusStopCreate = ({ onCreated }) => {
    const dataAPI = React.useContext(DataAPIContext);
    const { isFranchise, currentLicensee } = React.useContext(ContextStore);
    const history = useHistory();
    const { enqueueSnackbar } = useSnackbar();

    const [saving, setSaving] = React.useState(false);
    const [name, setName] = React.useState('');
    const [description, setDescription] = React.useState('');
    const [label, setLabel] = React.useState('');
    const [warning, setWarning] = React.useState('');
    const [latitude, setLatitude] = React.useState('');
    const [longitude, setLongitude] = React.useState('');
    const [tags, setTags] = React.useState([]);
    const [tourOperator, setTourOperator] = React.useState(currentLicensee || null);
    const [imageFile, setImageFile] = React.useState(null);
    const [imagePreview, setImagePreview] = React.useState(null);
    const [errors, setErrors] = React.useState({});

    const form = { name, label, description, warning, latitude, longitude, tags, tourOperator, imageFile };
    const [initialForm] = React.useState(() => formSnapshot(form));
    const hasChanges = !isEqual(formSnapshot(form), initialForm);
    const leaveWithoutPrompt = useLeavePrompt(hasChanges);

    const onLicenseeChange = (id) => {
        setTourOperator(id);
    };

    // X, CANCEL and the backdrop all land here; the leave prompt asks first.
    const close = () => history.push('/bus-tours/bus-stops');

    const onPickImage = (ev) => {
        const file = ev.target.files?.[0];
        if (file) {
            setImageFile(file);
            setImagePreview(URL.createObjectURL(file));
        }
    };

    const clearImage = () => {
        setImageFile(null);
        setImagePreview(null);
    };

    const onSave = () => {
        if (isFranchise && !tourOperator) {
            setErrors({ tour_operator: { error: true, helperText: 'Licensee is required' } });
            return;
        }
        setErrors({});
        const body = {
            name,
            label: label || null,
            description: description || null,
            warning: warning || null,
            latitude: latitude !== '' ? parseFloat(latitude) : null,
            longitude: longitude !== '' ? parseFloat(longitude) : null,
            tags: tags.map((t) => ({ id: t.id })),
            tour_operator: tourOperator,
        };
        const params = imageFile
            ? { data: body, files: { image: imageFile }, upload: true }
            : { data: body };

        // Success navigates away and unmounts the form, so only failures reset `saving`.
        setSaving(true);
        dataAPI(CREATE, 'route_stop', params)
            .then((res) => {
                if (res.statusCode && res.statusCode >= 400) {
                    enqueueSnackbar(res.data?.message || 'Error creating bus stop', { variant: 'error' });
                    setSaving(false);
                } else {
                    enqueueSnackbar('Bus stop created', { variant: 'success' });
                    if (onCreated) onCreated();
                    leaveWithoutPrompt(close);
                }
            })
            .catch(() => {
                enqueueSnackbar('Error creating bus stop', { variant: 'error' });
                setSaving(false);
            });
    };

    return (
        <>
            <PageMeta title="Create New Bus Stop" />
            <FixedLinearProgress visibility={saving} />
            <Drawer
                anchor="right"
                open
                onClose={close}
                PaperProps={{ sx: { width: { xs: '100%', sm: 400 }, overflowY: 'auto' } }}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <Box
                        sx={{
                            bgcolor: '#f5f5f5',
                            px: 2,
                            py: 2.5,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            position: 'sticky',
                            top: 0,
                            zIndex: 2,
                        }}
                    >
                        <Typography variant="h6" sx={{ fontWeight: 500 }}>Create New Bus Stop</Typography>
                        <IconButton onClick={close} aria-label="close">
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <FormLicenseeContext.Provider value={tourOperator}>
                    <Box sx={{ flex: 1, p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <LicenseeFormField
                            value={tourOperator}
                            onChange={onLicenseeChange}
                            error={errors.tour_operator?.error}
                            helperText={errors.tour_operator?.helperText}
                        />
                        <TextField
                            variant="filled"
                            label="Stop Name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            fullWidth
                            size="small"
                            required
                        />
                        <TextField
                            variant="filled"
                            label="Pin Label"
                            value={label}
                            onChange={(e) => setLabel(e.target.value.slice(0, 3))}
                            fullWidth
                            size="small"
                            inputProps={{ maxLength: 3 }}
                        />
                        <LatLngPicker
                            latitude={latitude}
                            longitude={longitude}
                            mapHeight={175}
                            onUpdate={({ latitude: lat, longitude: lng }) => {
                                setLatitude(lat ?? '');
                                setLongitude(lng ?? '');
                            }}
                        />
                        <TextField
                            variant="filled"
                            label="Description (Optional)"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            fullWidth
                            size="small"
                            multiline
                            minRows={3}
                        />
                        <TextField
                            variant="filled"
                            label="Warning"
                            value={warning}
                            onChange={(e) => setWarning(e.target.value)}
                            fullWidth
                            size="small"
                            multiline
                            minRows={2}
                        />

                        <SystemTagsDropdown
                            label="Tags"
                            value={tags}
                            onChange={(ev, value) => setTags(value)}
                        />

                        {imagePreview ? (
                            <Box sx={{ position: 'relative', borderRadius: 1, overflow: 'hidden' }}>
                                <Image
                                    src={imagePreview}
                                    cover
                                    height={200}
                                    width="100%"
                                    style={{ borderRadius: 8, display: 'block' }}
                                />
                                <Box sx={{ position: 'absolute', top: 11, right: 11 }}>
                                    <DeleteIconButton title="Delete image" overImage onClick={clearImage} />
                                </Box>
                                <ImageActionButton hasImage overImage component="label" sx={{ position: 'absolute', bottom: 12, right: 12 }}>
                                    <input type="file" hidden accept="image/*" onChange={onPickImage} />
                                </ImageActionButton>
                            </Box>
                        ) : (
                            <ImageActionButton component="label" sx={{ alignSelf: 'flex-start' }}>
                                <input type="file" hidden accept="image/*" onChange={onPickImage} />
                            </ImageActionButton>
                        )}
                    </Box>
                    </FormLicenseeContext.Provider>

                    <Box
                        sx={{
                            borderTop: '1px solid #d8d8d8',
                            p: 2,
                            display: 'flex',
                            gap: 1,
                            position: 'sticky',
                            bottom: 0,
                            bgcolor: 'background.paper',
                            zIndex: 2,
                        }}
                    >
                        <FormActionButtons
                            onSubmit={onSave}
                            submitDisabled={saving || !name}
                            onCancel={close}
                            size="medium"
                            sx={{ flex: 1 }}
                        />
                    </Box>
                </Box>
            </Drawer>
        </>
    );
};

export default BusStopCreate;
